import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { DataTableMobileProps } from '@/models/ui';
import './DataTableMobile.css';

const DataTableMobile = <T extends Record<string, any>>({
  data,
  columns,
  actions = [],
  loading = false,
  emptyMessage = 'No data available',
  onActionClick,
}: DataTableMobileProps<T>) => {
  const filtered = useMemo(() => data || [], [data]);

  const getDisplayValue = (item: T, col: any): React.ReactNode => {
    if (!col) return '-';
    const raw = item[col.id];
    if (col.format) return col.format(raw, item) ?? '-';
    return raw != null ? String(raw) : '-';
  };

  const getStringValue = (item: T, col: any): string => {
    if (!col) return '';
    const raw = item[col.id];
    if (col.format) {
      const r = col.format(raw, item);
      if (typeof r === 'string' || typeof r === 'number') return String(r);
      return '';
    }
    return raw != null ? String(raw) : '';
  };

  const findChip = (item: T): React.ReactNode => {
    for (let i = 1; i < Math.min(columns.length, 7); i++) {
      const v = getDisplayValue(item, columns[i]);
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        return v as React.ReactNode;
      }
    }
    return null;
  };

  return (
    <Box className="ml-container">
      {loading ? (
        <>
          {[...Array(6)].map((_, i) => (
            <Box key={i} className="ml-row">
              <Box className="ml-row-left">
                <Skeleton variant="text" width="50%" height={18} />
                <Skeleton variant="text" width="70%" height={14} sx={{ mt: '2px' }} />
              </Box>
              <Box className="ml-row-right" sx={{ gap: '6px' }}>
                <Skeleton variant="rounded" width={48} height={20} />
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
            </Box>
          ))}
        </>
      ) : filtered.length === 0 ? (
        <Box className="ml-empty">
          <Typography className="ml-empty-text">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        filtered.map((item, i) => {
          const primaryText = getStringValue(item, columns[0]) || '-';
          const secondaryParts = [columns[1], columns[2]]
            .filter(Boolean)
            .map(col => getStringValue(item, col))
            .filter(v => v && v !== '-');
          const secondaryText = secondaryParts.join(' · ');
          const chipNode = findChip(item);

          return (
            <Box key={i} className="ml-row">
              <Box className="ml-row-left">
                <Typography className="ml-primary" noWrap>
                  {primaryText}
                </Typography>
                {secondaryText && (
                  <Typography className="ml-secondary" noWrap>
                    {secondaryText}
                  </Typography>
                )}
              </Box>
              <Box className="ml-row-right">
                {chipNode && (
                  <Box className="ml-chip">{chipNode}</Box>
                )}
                {actions.length > 0 && (
                  <Box className="ml-actions">
                    {actions.slice(0, 3).map((action, ai) => (
                      <Tooltip key={ai} title={action.label} arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onActionClick
                                ? onActionClick(action, item)
                                : action.onClick(item)
                            }
                            disabled={action.disabled?.(item)}
                            className={`ml-btn ${action.color || ''}`}
                          >
                            {action.icon}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default DataTableMobile;
